import { useEffect, useMemo, useRef, useState } from 'react';
import { DsInput } from '@/modules/internal-v2/ds';
import { useManufacturerCandidates } from '@/api/items/items.hooks';
import { ManufacturerCandidate } from '@/api/items/items.types';
import './manufacturer-picker.css';

interface ManufacturerPickerProps {
  /** Currently-selected manufacturer ID, or null when none. */
  value: number | null;
  /** Called when the user picks a candidate or clears the selection. */
  onChange: (value: number | null) => void;
  disabled?: boolean;
  /** Overrides the default "search …" placeholder. */
  placeholder?: string;
  /** Replaces the empty-state copy shown when nothing is selected. */
  emptyLabel?: string;
  /** Extra class on the root for context-specific sizing. */
  className?: string;
}

/**
 * Searchable picker for `items.manufacturer_id`. Mirrors the
 * `SalesPersonPicker` pattern — fetches the small candidate list once and
 * filters client-side. The input doubles as search box and textual
 * representation of the selection; clearing it returns the field to `null`.
 */
export function ManufacturerPicker({
  value,
  onChange,
  disabled,
  placeholder,
  emptyLabel,
  className,
}: ManufacturerPickerProps) {
  const { data: candidates, isPending } = useManufacturerCandidates();

  const selected = useMemo(
    () => candidates?.find((c) => c.id === value) ?? null,
    [candidates, value],
  );
  // While the dropdown is open we show the user's typed query; while closed
  // we derive the input value from the current selection so external `value`
  // changes are reflected without a setState-in-effect sync.
  const [typedQuery, setTypedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const query = open ? typedQuery : selected ? formatCandidate(selected) : '';
  const setQuery = setTypedQuery;
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const list = candidates ?? [];
    const term = query.trim().toLowerCase();
    if (
      !term ||
      term === (selected ? formatCandidate(selected).toLowerCase() : '')
    ) {
      return list;
    }
    return list.filter((c) => matchesQuery(c, term));
  }, [candidates, query, selected]);

  const handleSelect = (candidate: ManufacturerCandidate) => {
    onChange(candidate.id);
    setQuery(formatCandidate(candidate));
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  const inputPlaceholder = isPending
    ? 'Lade Hersteller…'
    : (placeholder ?? 'Hersteller suchen');

  return (
    <div
      className={`mfr-picker${className ? ` ${className}` : ''}`}
      ref={rootRef}
    >
      <div className="mfr-picker-input">
        <DsInput
          value={query}
          placeholder={inputPlaceholder}
          disabled={disabled || isPending}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setTypedQuery(selected ? formatCandidate(selected) : '');
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        />
        {value != null && !disabled && (
          <button
            type="button"
            className="mfr-picker-clear"
            aria-label="Auswahl aufheben"
            onClick={handleClear}
          >
            ×
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="mfr-picker-popover">
          {filtered.length === 0 ? (
            <div className="mfr-picker-empty">
              {isPending
                ? 'Lädt…'
                : query.trim()
                  ? 'Keine Treffer.'
                  : (emptyLabel ?? 'Keine Hersteller synchronisiert.')}
            </div>
          ) : (
            <ul className="mfr-picker-list">
              {filtered.slice(0, 50).map((candidate) => {
                const isSelected = candidate.id === value;
                return (
                  <li key={candidate.id}>
                    <button
                      type="button"
                      className={`mfr-picker-item${isSelected ? ' is-selected' : ''}`}
                      onClick={() => handleSelect(candidate)}
                    >
                      <span className="mfr-picker-row">
                        <span className="mfr-picker-name">
                          {candidate.name}
                        </span>
                        <span className="mfr-picker-count" title="Artikel">
                          {candidate.itemCount.toLocaleString('de-DE')}
                        </span>
                      </span>
                      <span className="mfr-picker-meta">
                        <span className="mfr-picker-code">#{candidate.id}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {filtered.length > 50 && (
                <li className="mfr-picker-overflow">
                  {filtered.length - 50} weitere Treffer — Suche verfeinern.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function formatCandidate(c: ManufacturerCandidate): string {
  return `${c.name} · ${c.itemCount.toLocaleString('de-DE')} Artikel`;
}

function matchesQuery(c: ManufacturerCandidate, term: string): boolean {
  if (String(c.id).includes(term)) return true;
  if (c.name.toLowerCase().includes(term)) return true;
  return false;
}
